'use client';

/**
 * useOverflowTooltip Hook
 *
 * Tracks whether the referenced element's content is being clipped by CSS
 * overflow rules (e.g., `text-overflow: ellipsis`). Pair with a Tooltip so the
 * full text is only revealed when it doesn't fit.
 *
 * @example
 * ```tsx
 * const { ref, isOverflowing } = useOverflowTooltip<HTMLDivElement>();
 * return (
 *   <Tooltip title={text} disableHoverListener={!isOverflowing}>
 *     <div ref={ref} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 *       {text}
 *     </div>
 *   </Tooltip>
 * );
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseOverflowTooltipOptions {
  /** Which axis to measure. @default 'x' */
  axis?: 'x' | 'y';
  /** Skip the measurement entirely (e.g., when content is known to fit). */
  disabled?: boolean;
}

export interface UseOverflowTooltipReturn<T extends HTMLElement> {
  ref: React.RefCallback<T>;
  isOverflowing: boolean;
}

export function useOverflowTooltip<T extends HTMLElement = HTMLElement>(
  options: UseOverflowTooltipOptions = {},
): UseOverflowTooltipReturn<T> {
  const { axis = 'x', disabled = false } = options;
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  // Stable ref callback — recreating it each render would force React to
  // detach/reattach (disconnect + re-observe) on every parent re-render.
  const ref = useCallback<React.RefCallback<T>>(
    (node) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node || disabled || typeof ResizeObserver === 'undefined') {
        if (disabled) setIsOverflowing(false);
        return;
      }
      const measure = () => {
        const overflowing = axis === 'y' ? node.scrollHeight > node.clientHeight : node.scrollWidth > node.clientWidth;
        setIsOverflowing((prev) => (prev === overflowing ? prev : overflowing));
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      observerRef.current = observer;
    },
    [axis, disabled],
  );

  return { ref, isOverflowing };
}
